import { useOrganization, useUser } from "@clerk/react";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import { useI18n } from "~/i18n";
import { isInviteEmail } from "../../onboardingStore";
import {
  createOrgMember,
  fetchOrgMembers,
  type OrgClerkRole,
  type OrgMember,
} from "../../orgMembers";
import {
  OrganizationMembersPanelView,
  type OrganizationInvitationRow,
  type OrganizationMemberRow,
} from "./organizationMembersPanelView";
import {
  buildClerkMemberAvatarMap,
  isSelfMember,
  resolveMemberPersonAvatar,
  resolveSelfAvatarSeed,
} from "../workspace/morrowMemberAvatarResolve";
import { ensureUserAvatarSeed, useUserProfile } from "../workspace/morrowUserProfileStore";

function memberDisplayName(
  publicUser:
    | {
        firstName?: string | null;
        lastName?: string | null;
        identifier?: string | null;
      }
    | null
    | undefined,
): string {
  const fullName = [publicUser?.firstName, publicUser?.lastName].filter(Boolean).join(" ").trim();
  return fullName || publicUser?.identifier?.trim() || "—";
}

/** Inline member + invitation management for the Organization settings page. */
export function OrganizationMembersPanel({ isAdmin }: { readonly isAdmin: boolean }) {
  const { t } = useI18n();
  const { user } = useUser();
  const userProfile = useUserProfile();
  const { organization, memberships, invitations } = useOrganization({
    memberships: { pageSize: 50 },
    invitations: { pageSize: 50 },
  });
  const [orgMembers, setOrgMembers] = useState<OrgMember[] | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgClerkRole>("org:member");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [invitePending, setInvitePending] = useState(false);
  const [actionKey, setActionKey] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    await organization?.reload();
    await memberships?.revalidate?.();
    await invitations?.revalidate?.();
    if (organization) {
      const rows = await fetchOrgMembers();
      setOrgMembers(rows ?? []);
    } else {
      setOrgMembers(null);
    }
  }, [invitations, memberships, organization]);

  useEffect(() => {
    ensureUserAvatarSeed();
  }, []);

  useEffect(() => {
    if (!organization) {
      setOrgMembers(null);
      return;
    }
    void fetchOrgMembers().then((rows) => {
      setOrgMembers(rows ?? []);
    });
  }, [organization]);

  const handleInvite = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!organization || !isAdmin) return;
      const nextEmail = email.trim().toLowerCase();
      if (!isInviteEmail(nextEmail)) {
        setInviteError(t("onboarding.inviteError"));
        return;
      }
      setInviteError(null);
      setInvitePending(true);
      try {
        // Invite via the backend (which sets the Clerk `redirectUrl` so the email
        // opens THIS app, not Clerk's hosted portal) — the client inviteMember
        // cannot set a redirect. The backend also persists the domain member row.
        const created = await createOrgMember({
          email: nextEmail,
          role,
          title: "",
          functionRole: "",
          description: "",
          defaultSensitivity: "normal",
        });
        if (!created) {
          setInviteError(t("settings.organization.inviteFailed"));
          return;
        }
        setEmail("");
        setRole("org:member");
        await refresh();
      } catch {
        setInviteError(t("settings.organization.inviteFailed"));
      } finally {
        setInvitePending(false);
      }
    },
    [email, isAdmin, organization, refresh, role, t],
  );

  const handleRoleChange = useCallback(
    async (userId: string, nextRole: OrgClerkRole) => {
      if (!organization || !isAdmin) return;
      setActionKey(`role:${userId}`);
      try {
        await organization.updateMember({ userId, role: nextRole });
        await refresh();
      } finally {
        setActionKey(null);
      }
    },
    [isAdmin, organization, refresh],
  );

  const handleRemove = useCallback(
    async (userId: string) => {
      if (!organization || !isAdmin) return;
      setActionKey(`remove:${userId}`);
      try {
        await organization.removeMember(userId);
        await refresh();
      } finally {
        setActionKey(null);
      }
    },
    [isAdmin, organization, refresh],
  );

  const handleRevoke = useCallback(
    async (invitationId: string) => {
      if (!organization || !isAdmin) return;
      const invitation = invitations?.data?.find((entry) => entry.id === invitationId);
      if (!invitation) return;
      setActionKey(`revoke:${invitationId}`);
      try {
        await invitation.revoke();
        await refresh();
      } finally {
        setActionKey(null);
      }
    },
    [invitations?.data, isAdmin, organization, refresh],
  );

  const members = useMemo((): OrganizationMemberRow[] => {
    const selfEmail = user?.primaryEmailAddress?.emailAddress?.trim() ?? "";
    const selfName =
      user?.fullName?.trim() ||
      user?.primaryEmailAddress?.emailAddress?.trim() ||
      t("sidebar.account");
    const selfAvatarSeed = resolveSelfAvatarSeed(userProfile.avatarSeed, selfName);
    const clerkPortraitMap = buildClerkMemberAvatarMap(memberships?.data, user);
    const orgMemberByEmail = new Map(
      (orgMembers ?? []).map((entry) => [entry.email.trim().toLowerCase(), entry]),
    );

    return (memberships?.data ?? []).flatMap((entry) => {
      const publicUser = entry.publicUserData;
      const userId = publicUser?.userId;
      if (!userId) return [];
      const name = memberDisplayName(publicUser);
      const email = publicUser?.identifier?.trim() || "";
      const dbMember = orgMemberByEmail.get(email.toLowerCase());
      const avatar = resolveMemberPersonAvatar({
        email,
        userId,
        name,
        clerkPortraitMap,
        isSelf: isSelfMember({ userId, email }, user?.id, selfEmail),
        selfAvatarSeed,
        ...(dbMember?.avatarSeed ? { avatarSeed: dbMember.avatarSeed } : {}),
      });
      return [
        {
          id: entry.id,
          userId,
          name,
          email,
          role: entry.role as OrgClerkRole,
          ...avatar,
        },
      ];
    });
  }, [memberships?.data, orgMembers, t, user, userProfile.avatarSeed]);

  const pendingInvites = useMemo((): OrganizationInvitationRow[] => {
    return (invitations?.data ?? [])
      .filter((invitation) => invitation.status === "pending")
      .map((invitation) => ({
        id: invitation.id,
        emailAddress: invitation.emailAddress,
        role: invitation.role as OrgClerkRole,
      }));
  }, [invitations?.data]);

  return (
    <OrganizationMembersPanelView
      isAdmin={isAdmin}
      members={members}
      invitations={pendingInvites}
      membersLoading={memberships?.isLoading ?? true}
      email={email}
      role={role}
      inviteError={inviteError}
      invitePending={invitePending}
      actionKey={actionKey}
      onEmailChange={(value) => {
        setEmail(value);
        if (inviteError) setInviteError(null);
      }}
      onRoleChange={setRole}
      onInvite={(event) => void handleInvite(event)}
      onMemberRoleChange={(userId, nextRole) => void handleRoleChange(userId, nextRole)}
      onRemoveMember={(userId) => void handleRemove(userId)}
      onRevokeInvitation={(invitationId) => void handleRevoke(invitationId)}
    />
  );
}
